import React, { useCallback, useEffect, useRef, useState } from "react";
import { StyleSheet, Text, TextInput, View } from "react-native";
import { Feather, MaterialIcons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";

import Colors from "../../../../Resources/Color";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import Tooltip from "../../../Components/Tooltip";
import {
  InlineNutritionSummary,
  QuantityControl,
  useResponsiveIngredientRowLayout,
} from "./FoodRowShared";
import NutritionConflictWarningIcon from "../NutritionConflictWarningIcon";
import { useDevice } from "../../../_layout";
import { MAX_NUTRITION_NAME_LENGTH } from "../../nutritionLimits";

function getSelectedFoodRowDragHandleElementProps(dragHandleProps) {
  if (!dragHandleProps) {
    return {};
  }

  if (dragHandleProps.dragHandleProps) {
    return dragHandleProps.dragHandleProps;
  }

  return {
    ...(dragHandleProps.attributes ?? {}),
    ...(dragHandleProps.listeners ?? {}),
  };
}

export function SelectedFoodRow({
  title,
  subtitle,
  quantityValue,
  onChangeQuantity,
  unitOptions,
  selectedUnitKey,
  onSelectUnit,
  referenceText,
  totals,
  compactLayout = false,
  actionSlot = null,
  footer = null,
  leadingSlot = null,
  dragHandleProps = null,
  isLast = false,
  children = null,
  showQuantityControl = true,
  showNutritionSummary = true,
  showNutritionSummaryCalories = true,
  nutritionSummaryLabel = null,
  warningTooltip = "",
  isAutoCalculated = false,
  autoCalculatedTooltip = "",
  quantityLinkTooltip = "",
  onToggleQuantityLink = null,
  showDivider = true,
  trimBottomPadding = false,
  isReadOnlyPreview = false,
  chromeVariant = "default",
  containerStyle = null,
  isTitleEditable = false,
  onRenameTitle = null,
}) {
  const [isDragHandleHovered, setIsDragHandleHovered] = useState(false);
  const [isTitleRowHovered, setIsTitleRowHovered] = useState(false);
  const [isTitleEditHovered, setIsTitleEditHovered] = useState(false);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(String(title ?? ""));
  const titleInputRef = useRef(null);
  const titleDraftRef = useRef(String(title ?? ""));
  const isTitleEditingRef = useRef(false);
  const skipNextTitleBlurCommitRef = useRef(false);
  const pendingTitleBlurTimerRef = useRef(null);
  const { isMobile } = useDevice();
  const { t } = useTranslation();
  const { handleRowLayout, shouldUseCompactLayout } =
    useResponsiveIngredientRowLayout(compactLayout);
  const dragHandleElementProps =
    getSelectedFoodRowDragHandleElementProps(dragHandleProps);
  const canEditTitle =
    isTitleEditable && typeof onRenameTitle === "function" && !isReadOnlyPreview;
  const shouldKeepTitleEditButtonVisible = isMobile;
  const shouldShowTitleEditButton =
    canEditTitle &&
    !isTitleEditing &&
    (isTitleRowHovered || shouldKeepTitleEditButtonVisible);
  const editFoodNameLabel = t("editFoodName-string", "Edit food name");

  useEffect(() => {
    if (isTitleEditing) {
      return;
    }

    const nextTitle = String(title ?? "");
    titleDraftRef.current = nextTitle;
    setTitleDraft(nextTitle);
  }, [isTitleEditing, title]);

  useEffect(() => {
    if (!isTitleEditing) {
      return;
    }

    const focusTimer = setTimeout(() => {
      titleInputRef.current?.focus?.();
    }, 0);

    return () => {
      clearTimeout(focusTimer);
    };
  }, [isTitleEditing]);

  useEffect(
    () => () => {
      if (pendingTitleBlurTimerRef.current) {
        clearTimeout(pendingTitleBlurTimerRef.current);
      }
    },
    [],
  );

  const startTitleEdit = useCallback(
    (event) => {
      if (!canEditTitle) {
        return;
      }
      if (isTitleEditingRef.current) {
        return;
      }

      event?.stopPropagation?.();
      const currentTitle = String(title ?? "");
      titleDraftRef.current = currentTitle;
      setTitleDraft(currentTitle);
      skipNextTitleBlurCommitRef.current = false;
      isTitleEditingRef.current = true;
      setIsTitleRowHovered(false);
      setIsTitleEditHovered(false);
      setIsTitleEditing(true);
    },
    [canEditTitle, title],
  );

  const handleTitleDraftChange = useCallback((value) => {
    const nextValue = String(value ?? "").slice(0, MAX_NUTRITION_NAME_LENGTH);
    titleDraftRef.current = nextValue;
    setTitleDraft(nextValue);
  }, []);

  const commitTitleEdit = useCallback(() => {
    if (!isTitleEditingRef.current) {
      return;
    }

    if (pendingTitleBlurTimerRef.current) {
      clearTimeout(pendingTitleBlurTimerRef.current);
      pendingTitleBlurTimerRef.current = null;
    }

    const previousTitle = String(title ?? "");
    const nextTitle = titleDraftRef.current.trim();
    isTitleEditingRef.current = false;
    skipNextTitleBlurCommitRef.current = true;
    setIsTitleEditing(false);

    if (!nextTitle) {
      titleDraftRef.current = previousTitle;
      setTitleDraft(previousTitle);
      return;
    }

    if (nextTitle !== previousTitle.trim()) {
      onRenameTitle?.(nextTitle);
    }

    titleDraftRef.current = nextTitle;
    setTitleDraft(nextTitle);
  }, [onRenameTitle, title]);

  const cancelTitleEdit = useCallback(() => {
    if (!isTitleEditingRef.current) {
      return;
    }

    if (pendingTitleBlurTimerRef.current) {
      clearTimeout(pendingTitleBlurTimerRef.current);
      pendingTitleBlurTimerRef.current = null;
    }

    const previousTitle = String(title ?? "");
    isTitleEditingRef.current = false;
    skipNextTitleBlurCommitRef.current = true;
    titleDraftRef.current = previousTitle;
    setTitleDraft(previousTitle);
    setIsTitleEditing(false);
  }, [title]);

  const handleTitleInputBlur = useCallback(() => {
    if (skipNextTitleBlurCommitRef.current) {
      skipNextTitleBlurCommitRef.current = false;
      return;
    }

    if (pendingTitleBlurTimerRef.current) {
      clearTimeout(pendingTitleBlurTimerRef.current);
    }

    pendingTitleBlurTimerRef.current = setTimeout(() => {
      pendingTitleBlurTimerRef.current = null;
      commitTitleEdit();
    }, 80);
  }, [commitTitleEdit]);

  const handleTitleInputKey = useCallback(
    (event) => {
      const key = event?.nativeEvent?.key ?? event?.key;
      if (key === "Enter") {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        commitTitleEdit();
        return;
      }

      if (key === "Escape" || key === "Esc") {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        cancelTitleEdit();
      }
    },
    [cancelTitleEdit, commitTitleEdit],
  );

  return (
    <View
      style={[
        styles.selectedIngredientCard,
        !showDivider ? styles.selectedIngredientCardNoDivider : null,
        trimBottomPadding ? styles.selectedIngredientCardNoBottomPadding : null,
        isLast ? styles.selectedIngredientCardLast : null,
        containerStyle,
      ]}
    >
      <View
        onLayout={handleRowLayout}
        style={[
          styles.selectedIngredientShellRow,
          shouldUseCompactLayout ? styles.selectedIngredientShellRowCompact : null,
        ]}
      >
        {dragHandleProps ? (
          <View style={styles.selectedIngredientHandleRail}>
            <div
              style={styles.dragHandleContainer}
              onMouseEnter={() => !isMobile && setIsDragHandleHovered(true)}
              onMouseLeave={() => setIsDragHandleHovered(false)}
              {...dragHandleElementProps}
            >
              <MaterialIcons
                name="drag-indicator"
                size={16}
                color={
                  dragHandleProps.isDragging || isDragHandleHovered
                    ? Colors.darkgrayColor
                    : Colors.grayColor
                }
                style={styles.dragHandleIcon}
              />
            </div>
          </View>
        ) : null}

        {leadingSlot ? <View style={styles.leadingRail}>{leadingSlot}</View> : null}

        <View style={styles.selectedIngredientContentColumn}>
          <View
            style={[
              styles.selectedIngredientMainRow,
              shouldUseCompactLayout ? styles.selectedIngredientMainRowCompact : null,
            ]}
          >
            <View
              style={[
                styles.selectedIngredientIdentity,
                shouldUseCompactLayout
                  ? styles.selectedIngredientIdentityCompact
                  : styles.selectedIngredientIdentityDesktop,
              ]}
            >
              {showQuantityControl ? (
                <QuantityControl
                  value={quantityValue}
                  onChangeText={onChangeQuantity}
                  unitOptions={unitOptions}
                  selectedUnitKey={selectedUnitKey}
                  onSelectUnit={onSelectUnit}
                  referenceText={referenceText}
                  isAutoCalculated={isAutoCalculated}
                  autoCalculatedTooltip={autoCalculatedTooltip}
                  quantityLinkTooltip={quantityLinkTooltip}
                  onToggleQuantityLink={onToggleQuantityLink}
                  isReadOnly={isReadOnlyPreview}
                  chromeVariant={chromeVariant}
                />
              ) : null}
              <View style={styles.ingredientInfo}>
                <View
                  style={styles.ingredientNameRow}
                  onMouseEnter={
                    canEditTitle && !isMobile
                      ? () => setIsTitleRowHovered(true)
                      : undefined
                  }
                  onMouseLeave={
                    canEditTitle ? () => setIsTitleRowHovered(false) : undefined
                  }
                >
                  {isTitleEditing ? (
                    <View
                      style={styles.ingredientNameInputGuard}
                      onMouseDown={(event) => event.stopPropagation?.()}
                      onClick={(event) => event.stopPropagation?.()}
                      onTouchStart={(event) => event.stopPropagation?.()}
                    >
                      <TextInput
                        ref={titleInputRef}
                        value={titleDraft}
                        onChangeText={handleTitleDraftChange}
                        maxLength={MAX_NUTRITION_NAME_LENGTH}
                        onBlur={handleTitleInputBlur}
                        onKeyDown={handleTitleInputKey}
                        onKeyPress={handleTitleInputKey}
                        onSubmitEditing={commitTitleEdit}
                        blurOnSubmit={true}
                        returnKeyType="done"
                        selectTextOnFocus={true}
                        style={[styles.ingredientName, styles.ingredientNameInput]}
                        placeholder={String(title ?? "")}
                        placeholderTextColor={Colors.grayColor}
                        accessibilityLabel={editFoodNameLabel}
                        aria-label={editFoodNameLabel}
                      />
                    </View>
                  ) : (
                    <Text numberOfLines={1} style={styles.ingredientName}>
                      {title}
                    </Text>
                  )}
                  {canEditTitle && !isTitleEditing ? (
                    <Tooltip
                      content="editFoodName-string"
                      placement="top"
                    >
                      <HoverableTouchableOpacity
                        activeOpacity={0.85}
                        style={[
                          styles.inlineTitleEditButton,
                          !shouldShowTitleEditButton ? styles.hiddenInlineButton : null,
                        ]}
                        hoverStyle={undefined}
                        pointerEvents={shouldShowTitleEditButton ? "auto" : "none"}
                        title={editFoodNameLabel}
                        accessibilityLabel={editFoodNameLabel}
                        onPress={startTitleEdit}
                        onMouseEnter={() =>
                          !isMobile && setIsTitleEditHovered(true)
                        }
                        onMouseLeave={() =>
                          !isMobile && setIsTitleEditHovered(false)
                        }
                      >
                        <Feather
                          name="edit-2"
                          size={12}
                          color={Colors.grayColor}
                          style={{ opacity: isTitleEditHovered ? 0.7 : 1 }}
                        />
                      </HoverableTouchableOpacity>
                    </Tooltip>
                  ) : null}
                  <NutritionConflictWarningIcon
                    tooltip={warningTooltip}
                    size={13}
                    style={styles.warningIcon}
                  />
                </View>
                <Text numberOfLines={1} style={styles.ingredientSource}>
                  {subtitle}
                </Text>
              </View>
            </View>

            <View
              style={[
                styles.selectedIngredientUtilityRow,
                shouldUseCompactLayout
                  ? styles.selectedIngredientUtilityRowCompact
                  : null,
              ]}
            >
              {showNutritionSummary ? (
                <View
                  style={[
                    styles.selectedIngredientNutrition,
                    shouldUseCompactLayout
                      ? styles.selectedIngredientNutritionCompact
                      : null,
                  ]}
                >
                  {nutritionSummaryLabel ? (
                    <Text style={styles.nutritionSummaryLabel}>
                      {nutritionSummaryLabel}
                    </Text>
                  ) : null}
                  <InlineNutritionSummary
                    calories={totals?.calories}
                    fats={totals?.fats}
                    carbs={totals?.carbs}
                    protein={totals?.protein}
                    showCalories={showNutritionSummaryCalories}
                  />
                </View>
              ) : null}
              {actionSlot}
            </View>
          </View>
          {children}
        </View>
      </View>
      {footer}
    </View>
  );
}

const styles = StyleSheet.create({
  selectedIngredientCard: {
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: "rgba(63, 123, 141, 0.12)",
  },
  selectedIngredientCardNoDivider: {
    borderBottomWidth: 0,
  },
  selectedIngredientCardNoBottomPadding: {
    paddingBottom: 0,
  },
  selectedIngredientCardLast: {
    borderBottomWidth: 0,
  },
  dragHandleContainer: {
    padding: 4,
    cursor: "grab",
    touchAction: "none",
  },
  selectedIngredientHandleRail: {
    width: 24,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  dragHandleIcon: {
    transition: "color 200ms ease-in-out",
  },
  leadingRail: {
    width: 28,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  selectedIngredientShellRow: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: 10,
    minWidth: 0,
  },
  selectedIngredientShellRowCompact: {
    alignItems: "stretch",
  },
  selectedIngredientContentColumn: {
    flex: 1,
    minWidth: 0,
  },
  selectedIngredientMainRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flexWrap: "nowrap",
  },
  selectedIngredientMainRowCompact: {
    alignItems: "flex-start",
    flexWrap: "wrap",
    rowGap: 4,
  },
  selectedIngredientIdentity: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 320,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  selectedIngredientIdentityDesktop: {
    flexWrap: "nowrap",
  },
  selectedIngredientIdentityCompact: {
    flex: 1,
    width: "100%",
    flexWrap: "wrap",
    alignItems: "flex-start",
    rowGap: 4,
  },
  ingredientInfo: {
    flex: 1,
    minWidth: 0,
  },
  ingredientNameRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 6,
    minHeight: 20,
    minWidth: 0,
  },
  ingredientName: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "500",
    color: Colors.blackColor,
    flexShrink: 1,
  },
  ingredientNameInputGuard: {
    flex: 1,
    minWidth: 0,
  },
  ingredientNameInput: {
    width: "100%",
    minWidth: 0,
    height: 18,
    paddingHorizontal: 0,
    paddingVertical: 0,
    outlineStyle: "none",
  },
  inlineTitleEditButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  hiddenInlineButton: {
    opacity: 0,
  },
  ingredientSource: {
    marginTop: 3,
    fontSize: 10,
    lineHeight: 12,
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.45,
  },
  warningIcon: {
    flexShrink: 0,
  },
  selectedIngredientUtilityRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    marginLeft: "auto",
    maxWidth: "100%",
    minWidth: 0,
    flexShrink: 1,
  },
  selectedIngredientUtilityRowCompact: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 0,
  },
  selectedIngredientNutrition: {
    minWidth: 0,
    flexShrink: 1,
    alignItems: "flex-end",
  },
  selectedIngredientNutritionCompact: {
    flex: 1,
    minWidth: 0,
    alignItems: "flex-start",
  },
  nutritionSummaryLabel: {
    fontSize: 10,
    lineHeight: 13,
    fontWeight: "400",
    color: Colors.grayColor,
    marginBottom: 4,
  },
});
